import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import { BlockFormConfirmHardBlock } from '@eika-kortbetjening/block-form-confirm-hard-block';
import { BlockReorderLoader } from '@eika-kortbetjening/block-form-confirm-hard-block/lib/types';
import { Button } from '@eika/button';
import { ErrorCard, SuccessCard } from '@eika/card';
import { Modal } from '@eika/modal';

import SkeletonList from '@/components/skeleton/SkeletonList';
import genericErrors from '@/config/dictionary/sections/generic-errors';
import piwik from '@/piwik/piwik';
import { useDeleteCard, useHardBlockCard } from '@/react-query/hooks/mutation/use-block-card';
import { useAccount } from '@/react-query/hooks/query/use-account';
import { useCard } from '@/react-query/hooks/query/use-card';
import { useCustomer } from '@/react-query/hooks/query/use-customer';
import { goToRoot } from '@/utils/url-utils';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getRootUrl } from '@/utils/url/get-url-utils';

import { Format, useLinkProducer, useNavigate } from '@domain/app';
import { HardBlockReason } from '@generated-types';
import { usePinChangedQuery } from '@hooks/query/use-pin-changed-query';
import useReasonQuery from '@hooks/query/use-reason-query';
import { useReorderQuery } from '@hooks/query/use-reorder-query';
import useText from '@hooks/use-text';
import { formatAccountNumber, isUser } from '@utils/account-utils';

import { getHardBlockRequest } from './functions';

const HardBlockConfirm = () => {
    const text = useText();
    const { data: card, isLoading: isLoadingCard } = useCard();
    const {
        mutate: hardBlock,
        isPending: isHardBlockingCard,
        isError: failedHardBlockingCard,
        isSuccess: hasHardBlockedCard,
    } = useHardBlockCard();

    const {
        mutate: deleteCard,
        isPending: isDeletingCard,
        isError: failedDeletingCard,
        isSuccess: hasDeletedCard,
    } = useDeleteCard();

    const navigate = useNavigate();
    const linkProducer = useLinkProducer();

    const accountQuery = useAccount(card);
    const account = accountQuery?.data;
    const isLoadingBillingAccount = accountQuery?.isLoading;
    const { data: owner, isLoading: isLoadingOwner, isError } = useCustomer(card);
    const user = isUser(owner);

    const reasonQuery = useReasonQuery();
    const pinChangedQuery = usePinChangedQuery();
    const reorderQuery = useReorderQuery();
    const [modalOpen, setModalOpen] = useState(false);

    useEffect(() => {
        if (hasHardBlockedCard) {
            setModalOpen(true);
        }
    }, [hasHardBlockedCard]);

    const blockReorderLoader: BlockReorderLoader = useMemo(
        () => ({
            isHardBlockingCard: isHardBlockingCard || isDeletingCard,
            hasHardBlockedCard: hasHardBlockedCard || hasDeletedCard,
            failedHardBlockingCard: failedHardBlockingCard || failedDeletingCard,
            failedReorderingCard: false,
            hasReorderedCard: false,
            isReorderingCard: false,
        }),
        [
            isHardBlockingCard,
            isDeletingCard,
            hasHardBlockedCard,
            hasDeletedCard,
            failedHardBlockingCard,
            failedDeletingCard,
        ],
    );

    if (isError) {
        return <ErrorCard title={genericErrors.genericErrorHeader} />;
    }

    if ((typeof card === 'undefined' || isLoadingCard || isLoadingBillingAccount) ?? isLoadingOwner) {
        return <SkeletonList />;
    }

    if (reasonQuery === null) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    const submit = () => {
        if (reasonQuery === HardBlockReason.NOT_IN_USE) {
            deleteCard({ cardType: card.type, cardId: card.id });
            return;
        }

        hardBlock(
            getHardBlockRequest({ card, reason: reasonQuery, reorder: reorderQuery, pinChanged: pinChangedQuery }),
        );
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    const foundCardAccountPhoneNumber = user?.contact.mobile;
    const formattedPhoneNumber = foundCardAccountPhoneNumber
        ? Format.phoneNumber(foundCardAccountPhoneNumber)
        : undefined;

    const confirmBySmsPhoneNumber = reasonQuery === HardBlockReason.NOT_IN_USE ? undefined : formattedPhoneNumber;

    const accountName = () => {
        if (!account) {
            return '';
        }

        if ('agreementName' in account) {
            return account.agreementName;
        }

        if ('product' in account) {
            return account.productName;
        }

        return '';
    };

    return (
        <>
            <Modal setOpen={setModalOpen} open={modalOpen} onClose={() => goToRoot(navigate)}>
                <SuccessCard
                    title={reorderQuery ? text.orderCardReplaceComplete : text.blockStatusHard}
                    centeredWithMargin
                >
                    <p>{reorderQuery ? text.orderCardStepDescription : null}</p>
                    <Button onClick={() => goToRoot(navigate)}>Ok</Button>
                </SuccessCard>
            </Modal>

            <BlockFormConfirmHardBlock
                accountName={accountName() ?? ''}
                blockReorderLoader={blockReorderLoader}
                cancel={cancel}
                formattedAccountNumber={formatAccountNumber(card.accountNumber)}
                hardBlockReason={reasonQuery}
                formattedCardNumber={Format.cardNumber(card.number)}
                formattedConfirmBySmsPhoneNumber={confirmBySmsPhoneNumber}
                sitecoreDictionary={text}
                submit={submit}
                wishToChangePin={pinChangedQuery}
                wishToOrderNewCard={reorderQuery}
                piwik={piwik}
            />
        </>
    );
};

export default HardBlockConfirm;
